import type { LoaderFunctionArgs } from '@remix-run/node'
import {
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData
} from '@remix-run/react'
import { NuqsAdapter } from 'nuqs/adapters/remix'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useChangeLanguage } from 'remix-i18next/react'

import { ErrorPage } from '~/components/error-page'
import { RootScripts } from '~/components/root-scripts'
import { AppTypeProvider } from '~/contexts/app-type-context'
import { LayoutProvider } from '~/contexts/layout-context'
import { env, isProduction, appType, logoutTimeout } from '~/env.server'
import { useC2CHandler } from '~/hooks/common/use-c2c-handler'
import { useShopifyNavigate } from '~/hooks/common/use-shopify-navigate'
import {
  type AppTypeRequest,
  appTypeCookieKey,
  isQuickDemoEnabledCookieKey,
  logoutTimeoutCookieKey
} from '~/routes/api/app-type'
import i18next from '~/translations/i18next.server'
import { getCookie } from '~/utils'
import { addGtmScript } from '~/utils/gtm'

export const ErrorBoundary = () => (
  <html>
    <head>
      <Meta />
      <Links />
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width,initial-scale=1" />
    </head>
    <body>
      <ErrorPage />
      <Scripts />
    </body>
  </html>
)

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = await i18next.getLocale(request)

  const isDev = appType === 'dev'

  return json({
    ENV: {
      SENTRY_DSN: env.SENTRY_DSN || '',
      NODE_ENV: env.NODE_ENV,
      APP_VERSION: env.APP_VERSION
    },
    SHOPIFY_API_KEY: env.SHOPIFY_API_KEY,
    IS_WEB_VITALS_ENABLED: env.IS_WEB_VITALS_ENABLED,
    WEB_VITALS_METRICS_URL: `${env.SHOPIFY_APP_URL}/api/web-vitals-metrics`,
    GTM_ID: isProduction ? env.GTM_ID : undefined,
    locale,
    C2C: {
      c2cOrigin: env.IFRAME_URL,
      cookiePrefix: env.COOKIE_PREFIX
    },
    devSettings: {
      appType: (isDev
        ? getCookie(request, appTypeCookieKey, 'regular')
        : appType) as AppTypeRequest['appType'],
      isDev,
      logoutTimeout: parseInt(
        getCookie(request, logoutTimeoutCookieKey, logoutTimeout.toString())
      ),
      isQuickDemoEnabled: isDev
        ? getCookie(
            request,
            isQuickDemoEnabledCookieKey,
            String(env.IS_QUICK_DEMO_ENABLED)
          ) === 'true'
        : env.IS_QUICK_DEMO_ENABLED,
      envDefaults: {
        appType: 'regular' as const,
        logoutTimeout,
        isQuickDemoEnabled: env.IS_QUICK_DEMO_ENABLED
      }
    }
  })
}

export const handle = {
  i18n: 'common'
}

export default function App() {
  const {
    ENV,
    GTM_ID,
    locale,
    IS_WEB_VITALS_ENABLED,
    WEB_VITALS_METRICS_URL,
    devSettings,
    SHOPIFY_API_KEY,
    C2C
  } = useLoaderData<typeof loader>()
  const { i18n } = useTranslation()

  useChangeLanguage(locale)
  useShopifyNavigate()
  useC2CHandler(C2C)

  useEffect(() => {
    if (GTM_ID) {
      addGtmScript(GTM_ID)
    }
  }, [GTM_ID])

  return (
    <html lang={i18n.language} dir={i18n.dir()}>
      <head>
        <Meta />
        <Links />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="preconnect" href="https://cdn.shopify.com/" />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />
        {IS_WEB_VITALS_ENABLED && (
          <meta name="shopify-debug" content="web-vitals" />
        )}

        <meta name="shopify-api-key" content={SHOPIFY_API_KEY} />
        <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js" />
        <script src="https://cdn.shopify.com/shopifycloud/polaris.js" />
      </head>
      <body>
        <NuqsAdapter>
          <LayoutProvider>
            <AppTypeProvider
              options={{
                defaultLogoutTimeout: devSettings.logoutTimeout,
                defaultAppType: devSettings.appType,
                defaultIsQuickDemoEnabled: devSettings.isQuickDemoEnabled,
                isDev: devSettings.isDev,
                envDefaults: devSettings.envDefaults
              }}
            >
              <Outlet />
            </AppTypeProvider>
          </LayoutProvider>
        </NuqsAdapter>

        <ScrollRestoration />
        <Scripts />
        <RootScripts
          env={ENV}
          webVitalsMetricsUrl={WEB_VITALS_METRICS_URL}
          gtmId={GTM_ID}
        />
      </body>
    </html>
  )
}
